import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeId } from '../lib/theme-context';
import { DEFAULT_THEME_ID, themeOptions } from '../lib/theme';

/**
 * Colour theme picker, opened from the user menu. Modelled on click2gro's
 * SettingsDialog. A swatch applies immediately and is saved as it is picked,
 * so there is no separate Save step.
 */
export function ThemeDialog({ open, onClose }: { open: boolean; onClose: () => void }): React.JSX.Element {
    const { themeId, setThemeId } = useThemeId();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                Settings
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Theme Color
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Choose a colour theme for your dashboard
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                            gap: 2,
                        }}
                    >
                        {themeOptions.map((opt) => {
                            const selected = opt.id === themeId;
                            return (
                                <Box
                                    key={opt.id}
                                    component="button"
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => {
                                        setThemeId(opt.id);
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        p: 1,
                                        borderRadius: 2,
                                        bgcolor: 'transparent',
                                        color: 'text.primary',
                                        font: 'inherit',
                                        border: selected ? '3px solid' : '1px solid',
                                        borderColor: selected ? 'primary.main' : 'divider',
                                        transition: 'all 0.2s',
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                                    }}
                                >
                                    <Box sx={{ width: '100%', height: 52, borderRadius: 1, mb: 1, bgcolor: opt.swatch }} />
                                    <Typography variant="caption" sx={{ fontWeight: selected ? 700 : 400 }}>
                                        {opt.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            setThemeId(DEFAULT_THEME_ID);
                        }}
                    >
                        Reset to Default
                    </Button>
                    <Button variant="contained" onClick={onClose}>
                        Done
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
